import React, { useState, useEffect } from "react";
import { useSuperAdmin } from "../context/SuperAdminContext"

const CATEGORY_OPTIONS = [
  { key: "onboarding", label: "Onboarding" },
  { key: "refcheck", label: "Refcheck" },
  { key: "retention", label: "Retention" },
  { key: "companyScreening", label: "Company Screening" },
  { key: "eshram", label: "E-Shram" },
];

const HISTORY_FILTER_OPTIONS = [{ key: "", label: "All" }, ...CATEGORY_OPTIONS];

const CallHistorySection = ({ worker }) => {
  const { fetchWorkerVerificationHistory } = useSuperAdmin();

  const [historyFilter, setHistoryFilter] = useState(""); // "" = All
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  const loadHistory = async (filterOverride) => {
    if (!worker?.workerId) return;
    const filterToUse = filterOverride !== undefined ? filterOverride : historyFilter;

    setHistoryLoading(true);
    setHistoryError(false);
    try {
      const params = filterToUse ? { category: filterToUse } : {};
      const response = await fetchWorkerVerificationHistory(
        worker.workerId,
        worker.lastCompanyName,
        params
      );

      let list = [];
      if (response?.success && Array.isArray(response.data)) {
        list = response.data;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      } else if (Array.isArray(response)) {
        list = response;
      }

      setHistory(list);
    } catch (error) {
      console.error("Failed to load verification history:", error);
      setHistoryError(true);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilter, worker?.workerId]);

  const formatHistoryDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return "—";
    const datePart = d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
    const timePart = d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${datePart} | ${timePart}`;
  };

  const categoryLabel = (key) =>
    CATEGORY_OPTIONS.find((c) => c.key === key)?.label ?? key ?? "—";

  return (
    <div className="space-y-4">
      {/* Small category filter — only changes what's sent to the endpoint */}
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-800">
          {CATEGORY_OPTIONS.find((item) => item.key === historyFilter)?.label ||
            historyFilter}{" "}
          History
        </p>
        <select
          value={historyFilter}
          onChange={(e) => setHistoryFilter(e.target.value)}
          className="px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          {HISTORY_FILTER_OPTIONS.map((opt) => (
            <option key={opt.key || "all"} value={opt.key}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {historyLoading ? (
        <div className="flex justify-center py-6">
          <svg className="animate-spin h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        </div>
      ) : historyError ? (
        <div className="text-center py-4">
          <p className="text-red-500 text-sm mb-2">Failed to load history.</p>
          <button
            onClick={() => loadHistory()}
            className="text-indigo-600 text-sm underline hover:text-indigo-800"
          >
            Retry
          </button>
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-6 text-gray-500 text-sm border border-gray-200 rounded-lg">
          No history available for this worker.
        </div>
      ) : (
        <div className="space-y-3 pr-0.5">
          {history.map((record, index) => (
            <div
              key={record._id ?? index}
              className="bg-gray-50 rounded-lg px-4 py-3 flex gap-3"
            >
              <div className="flex-1 min-w-0 gap-y-2">
                {/* Meta row */}
                <div className="text-xs text-gray-500">
                  {record.category && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gradient-to-r from-indigo-50 to-violet-50 text-indigo-700 border border-indigo-100 font-medium text-[10px] leading-none tracking-wide uppercase">
                      <span className="w-1 h-1 rounded-full bg-indigo-500" />
                      {categoryLabel(record.category)}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-2">
                  <span>On {formatHistoryDateTime(record.createdAt ?? record.updatedAt)}</span>
                  {record.updatedBy && (
                    <span>
                      Created by:{" "}
                      <span className="text-gray-800 font-medium">
                        {typeof record.updatedBy === "object"
                          ? record.updatedBy.name || record.updatedBy.email || "—"
                          : record.updatedBy}
                      </span>
                    </span>
                  )}
                  {record.status && (
                    <span>
                      Status: <span className="text-gray-800 font-medium">{record.status}</span>
                    </span>
                  )}
                  {record.subStatus && (
                    <span>
                      Sub Status:{" "}
                      <span className="text-gray-800 font-medium">{record.subStatus}</span>
                    </span>
                  )}
                  {record.lastCompanyName && record.category === "retention" && (
                    <span>
                      Last Company:{" "}
                      <span className="text-gray-800 font-medium">{record.lastCompanyName}</span>
                    </span>
                  )}
                  {record.lastCompanyName && record.category === "companyScreening" && (
                    <span>
                      Company Selected:{" "}
                      <span className="text-gray-800 font-medium">{record.lastCompanyName}</span>
                    </span>
                  )}
                </div>

                {record.remarks && (
                  <div className="flex items-start gap-2 mt-1">
                    <p className="text-sm text-gray-500 whitespace-nowrap">Remarks:</p>
                    <p className="font-medium text-sm text-gray-800">{record.remarks}</p>
                  </div>
                )}

                {(() => {
                  const issueGroups = Object.entries(record || {}).filter(
                    ([_, value]) => Array.isArray(value) && value.length > 0
                  );

                  return issueGroups.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {issueGroups.map(([heading, issues]) => {
                        const formattedHeading = heading
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());

                        return (
                          <div key={heading} className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-gray-800">
                              {formattedHeading}:
                            </span>
                            {issues.map((issue, i) => (
                              <span
                                key={`${heading}-${issue}-${i}`}
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-600 border border-indigo-100"
                              >
                                {issue}
                              </span>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CallHistorySection;